import { useEffect, useState } from 'react'
import { ExternalLink, TrendingUp, Wallet, AlertTriangle } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, Stat, Select, RefreshButton, Tag, BarList } from '../ui'
import { get } from '../../lib/api'
import { clsx, compact, fa, num } from '../../lib/format'

export default function Treasury() {
  const [days, setDays] = useState('30')
  const [rev, setRev] = useState<any>(null)
  const [tr, setTr] = useState<any>(null)
  const [dist, setDist] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([
      get(`/admin/finance/revenue?days=${days}`).then(setRev),
      get('/admin/finance/treasury').then(setTr),
      get('/admin/asset-distribution').then(setDist),
    ]).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(load, [days])

  return (
    <>
      <PageHead title="خزانه و درآمد" subtitle="مقایسه بدهی به کاربران با موجودی کیف پول گرم و گزارش درآمد"
        action={
          <div className="flex gap-2">
            <Select value={days} onChange={setDays} options={[['7', '۷ روز'], ['30', '۳۰ روز'], ['90', '۹۰ روز']]} />
            <RefreshButton onClick={load} loading={loading} />
          </div>
        } />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="درآمد کل بازه" value={compact(rev?.totalIrt)} icon={<TrendingUp className="h-5 w-5" />} color="#00E39A" />
        <Stat label="کارمزد معاملات" value={compact(rev?.tradingFeeIrt)} color="#7C5CFF" />
        <Stat label="کارمزد تبدیل" value={compact(rev?.convertFeeIrt)} color="#22D3EE" />
        <Stat label="کارمزد برداشت" value={compact(rev?.withdrawFeeIrt)} color="#FFB020" />
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-2">
        <Panel title="درآمد به تفکیک ارز">
          <BarList items={(rev?.byCurrency ?? []).map((c: any) => ({
            label: `${c.currency} · ${num(c.amount, 6)}`, value: c.valueIrt,
          }))} format={compact} />
          {!(rev?.byCurrency ?? []).length && <p className="py-6 text-center text-xs text-slate-500">درآمدی ثبت نشده است</p>}
        </Panel>

        <Panel title="دارایی نگهداری‌شده کاربران">
          <BarList items={(dist?.assets ?? []).slice(0, 10).map((a: any) => ({
            label: `${a.currency} · ${num(a.amount, 4)}`, value: a.valueIrt,
          }))} format={compact} />
        </Panel>
      </div>

      <Panel title="پوشش دارایی (بدهی به کاربران در برابر موجودی کیف پول گرم)" dense
        action={<span className="num text-[11px] text-slate-500">
          مجموع بدهی: {compact(tr?.totalLiabilityIrt)} تومان
        </span>}>
        <div className="border-b border-white/[.06] px-4 py-3 text-[11px] leading-6 text-slate-500">
          کیف پول گرم فقط بخشی از دارایی را نگه می‌دارد؛ پوشش کمتر از ۱۰۰٪ لزوماً مشکل نیست،
          اما برای پرداخت برداشت‌ها باید موجودی کافی داشته باشید.
        </div>
        <DataTable rows={tr?.assets ?? null} minWidth={620} columns={[
          { key: 'c', header: 'ارز', cell: (r: any) => <span className="num text-slate-200">{r.currency}</span> },
          { key: 'l', header: 'بدهی به کاربران', cell: (r: any) => <span className="num text-white">{num(r.liability, 8)}</span> },
          { key: 'h', header: 'موجودی کیف پول گرم', cell: (r: any) => <span className="num text-slate-300">{num(r.hotBalance, 8)}</span> },
          {
            key: 'cov', header: 'پوشش', cell: (r: any) => r.coverage === null
              ? <Tag kind="muted">—</Tag>
              : <Tag kind={r.coverage >= 100 ? 'up' : r.coverage >= 20 ? 'gold' : 'down'}>{num(r.coverage, 1)}٪</Tag>,
          },
          { key: 'v', header: 'ارزش بدهی (تومان)', cell: (r: any) => <span className="num text-slate-400">{compact(r.liabilityIrt)}</span> },
        ]} />
      </Panel>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {(tr?.networks ?? []).map((n: any) => (
          <Panel key={n.network} title={
            <div className="flex items-center gap-2">
              <span className="font-bold text-white">{n.faName}</span>
              <Tag kind="muted">{n.network}</Tag>
            </div>
          } action={
            <a href={n.explorer} target="_blank" rel="noreferrer" className="btn-ghost !px-2.5 !py-1.5 text-[11px]">
              اکسپلورر <ExternalLink className="h-3 w-3" />
            </a>
          }>
            <div className="mb-3 rounded-xl border border-white/[.06] bg-white/[.02] p-3">
              <div className="mb-1 flex items-center gap-1.5 text-[10px] text-slate-500">
                <Wallet className="h-3 w-3" /> آدرس کیف پول گرم
              </div>
              <div className="num break-all text-[11px] text-slate-300" dir="ltr">{n.hotAddress}</div>
            </div>
            <div className="space-y-1.5">
              {n.balances.map((b: any) => (
                <div key={b.symbol} className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">{b.symbol}</span>
                  <span className={clsx('num', b.balance === null ? 'text-slate-600' : 'text-slate-200')}>
                    {b.balance === null ? 'در دسترس نیست' : num(b.balance, 8)}
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        ))}
        {!(tr?.networks ?? []).length && (
          <Panel>
            <div className="flex items-center gap-2 py-6 text-xs text-slate-500">
              <AlertTriangle className="h-4 w-4" /> شبکه فعالی برای نمایش موجودی وجود ندارد.
            </div>
          </Panel>
        )}
      </div>
    </>
  )
}
